import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { EMBARCACOES_TABLE_DENSITY_KEY } from "@/core/constants/keys";
import { Paths } from "@/core/constants/paths";
import { useTableSettings, useToast } from "@/ds";
import type { HubTableDataTableProps } from "@/ds";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useTableSettingsAppliedToast } from "@/shared/hooks";
import { clearAuthToken } from "@/modules/auth";
import type { Embarcacao } from "@/modules/embarcacoes";
import type { EmbarcacoesColumnKey } from "./embarcacoes.constants";
import {
  EMBARCACOES_ALL_COLUMNS,
  EMBARCACOES_MOBILE_QUERY,
  EMBARCACOES_TABLE_LABEL,
} from "./embarcacoes.constants";
import {
  buildEmbarcacaoLinha,
  buildEmbarcacoesColunas,
  sumColumnWidthsRem,
} from "./embarcacoes.utils";
import type { EmbarcacaoLinha } from "./embarcacoes.utils";
import { useEmbarcacoesExportacao } from "./hooks/use-embarcacoes-exportacao";
import { useEmbarcacoesFiltrosColunas } from "./hooks/use-embarcacoes-filtros-colunas";
import { useEmbarcacoesFiltrosSalvos } from "./hooks/use-embarcacoes-filtros-salvos";
import { useEmbarcacoesLista } from "./hooks/use-embarcacoes-lista";
import type { EmbarcacoesScreenViewModel } from "./types-embarcacoes";

export function useEmbarcacoesScreen(): EmbarcacoesScreenViewModel {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(EMBARCACOES_MOBILE_QUERY);
  const toast = useToast();
  // Quem abriu o detalhamento: ao fechar, o foco volta para ele.
  const detailTrigger = useRef<HTMLElement | null>(null);
  // A embarcação aberta não trafega na URL.
  const [detalhe, setDetalhe] = useState<Embarcacao | null>(null);

  const settings = useTableSettings<EmbarcacoesColumnKey>({
    densityKey: EMBARCACOES_TABLE_DENSITY_KEY,
    allColumns: EMBARCACOES_ALL_COLUMNS,
  });
  const notifySettingsApplied = useTableSettingsAppliedToast(toast);
  const filterColumns = useEmbarcacoesFiltrosColunas();
  const lista = useEmbarcacoesLista();
  const filtrosSalvos = useEmbarcacoesFiltrosSalvos(lista.applyFilters);
  const exportView = useEmbarcacoesExportacao({
    colunas: settings.visibleColumns,
    search: lista.search,
    filters: lista.filters,
    sort: lista.appliedSort,
    filterColumns,
    isEmpty: lista.status === "success" && lista.total === 0,
    toast,
  });

  const linhas = useMemo(() => lista.items.map(buildEmbarcacaoLinha), [lista.items]);
  const colunas = buildEmbarcacoesColunas(isMobile);
  const minWidthRem = sumColumnWidthsRem(colunas);
  // Layout fixo: abaixo da soma das larguras do Figma a tabela rola na horizontal.
  const dataTableProps: HubTableDataTableProps = {
    scrollable: true,
    scrollHeight: "flex",
    tableStyle: {
      tableLayout: "fixed",
      width: `max(100%, ${minWidthRem}rem)`,
      minWidth: `${minWidthRem}rem`,
    },
    "aria-label": EMBARCACOES_TABLE_LABEL,
  };

  /** Abre o detalhamento sobre a listagem, sem mexer em busca, filtros, ordenação nem página. */
  function abrirDetalhe(embarcacao: Embarcacao) {
    detailTrigger.current = document.activeElement as HTMLElement | null;
    setDetalhe(embarcacao);
  }

  function fecharDetalhe() {
    setDetalhe(null);
    detailTrigger.current?.focus();
    detailTrigger.current = null;
  }

  return {
    isMobile,
    search: lista.search,
    buscar: lista.buscar,
    filterColumns,
    filters: lista.filters,
    applyFilters: lista.applyFilters,
    filtrosSalvos,
    settings,
    exportView,
    notifySettingsApplied,
    tabela: {
      items: linhas,
      isFirstLoad: lista.status === "loading" && linhas.length === 0,
      total: lista.total,
      status: lista.status,
      errorMessage: lista.errorMessage,
      reload: lista.reload,
      table: lista.tabela,
      settings,
      colunas,
      dataTableProps,
      hasSearch: lista.hasSearch,
      hasFilters: lista.hasFilters,
      onClear: lista.hasSearch
        ? lista.clearSearch
        : lista.hasFilters
          ? lista.clearFilters
          : undefined,
      onDetail: (linha: EmbarcacaoLinha) => abrirDetalhe(linha.embarcacao),
    },
    detalhe,
    abrirDetalhe,
    fecharDetalhe,
    reautenticar: () => {
      clearAuthToken();
      navigate(Paths.LOGIN, { replace: true });
    },
  };
}
